import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CreateReturnDisabledReason } from './create-return-disabled-reason';
import { emptyArray } from 'ui-components/default-props/empty';
import { FulfillmentPlanOrderGridStatus } from '../../../../enums/fulfillmentplan-order-grid-status';
import Global from '../../../../store/global-selectors';
import { NewReturnFulfillmentId } from '../../hooks/use-pending-return';
import {
  ORDER_DETAILS_ACTIONS,
  useTrackOrderDetailsAction,
} from '../../../modals/order-details-modal/event-tracking/use-track-order-details-action';
import { useReturnNavigation } from '../../hooks/use-return-navigation';
import { useCanCreateEnhancedReturn } from '../../../../store/returns/returns.helpers';
import { useEnhancedReturnsContext } from '../../context/enhanced-returns-context-provider';
import { useTrackWorkflow, WorkflowSteps } from '../../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../../tracking/tracking-constants';

const doesSalesOrderHaveShippedFulfillmentPlanSelector = (state, salesOrderId) => {
  const fulfillmentPlanIds =
    Global.selectEntitiesSalesOrderProperty(state, {
      salesOrderId,
      property: 'fulfillmentPlanIds',
    }) || emptyArray;

  const fulfillmentPlans =
    Global.selectEntitiesFulfillmentPlansByIds(state, {
      fulfillmentPlanIds,
    }) || emptyArray;

  return fulfillmentPlans?.some(({ orderGridStatus }) => {
    return orderGridStatus === FulfillmentPlanOrderGridStatus.Shipped.name;
  });
};

export const useCreateReturn = (salesOrderId, source) => {
  const shouldUseEnhancedReturn = useCanCreateEnhancedReturn(salesOrderId);

  const { navigateToReturn } = useReturnNavigation();
  const { registerPendingReturn } = useEnhancedReturnsContext();
  const trackCreateReturn = useTrackWorkflow(Workflows.CREATE_RETURN_FLOW);
  const trackCreateReturnClick = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.CREATE_RETURN);

  const createReturnDisabledReason = useSelector((state) => {
    if (!doesSalesOrderHaveShippedFulfillmentPlanSelector(state, salesOrderId)) {
      return CreateReturnDisabledReason.IsUnshipped;
    }
    return null;
  });

  const createReturn = useCallback(() => {
    trackCreateReturn(WorkflowSteps.START, {
      enhanced: shouldUseEnhancedReturn,
      salesOrderId,
      source,
    });
    trackCreateReturnClick();
    navigateToReturn(salesOrderId, NewReturnFulfillmentId, shouldUseEnhancedReturn);
    if (shouldUseEnhancedReturn) {
      registerPendingReturn();
    }
  }, [navigateToReturn, salesOrderId, source, trackCreateReturn, trackCreateReturnClick, shouldUseEnhancedReturn, registerPendingReturn]);

  return useMemo(() => {
    return {
      createReturn,
      isCreateReturnDisabled: !!createReturnDisabledReason,
      createReturnDisabledReason,
    };
  }, [createReturn, createReturnDisabledReason]);
};
